import { User } from '../types';
import { api } from './ApiClient';

class UserService {
  async getUserInfo(userToken: string): Promise<User> {
    try {
      const response = await api.post<{ success: boolean; message: string; result: User }>(
        '/api/user/GetUserInfor',
        null,
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '사용자 정보 조회에 실패했습니다.');
      }
      
      return response.result;
    } catch (error) {
      // 사용자 정보 조회 실패 - 프로필 정보 로드 문제 진단을 위해 로그 유지
      console.error('사용자 정보 조회 실패:', error);
      throw error;
    }
  }

  async updateUserInfo(userToken: string, data: {
    loginName: string;
    maxSelectNumber: string | number;
    includeNumbers?: number[];
    excludeNumbers?: number[];
    phoneNumber?: string;
    isMailSend?: boolean;
  }): Promise<User> {
    try {
      const params: Record<string, unknown> = {
        login_name: data.loginName,
        max_select_number: data.maxSelectNumber.toString(),
        phone_number: data.phoneNumber || '',
        is_mail_send: data.isMailSend !== undefined ? data.isMailSend : true
      };

      // 포함/제외 희망 번호는 호출자가 제공한 경우에만 전송 (부분 업데이트가 기존 선호를 덮어쓰지 않도록).
      // 서버는 include_numbers 우선, 없으면 digit1/2/3(구 MAUI) 호환 경로로 처리.
      if (data.includeNumbers !== undefined) params.include_numbers = JSON.stringify(data.includeNumbers);
      if (data.excludeNumbers !== undefined) params.exclude_numbers = JSON.stringify(data.excludeNumbers);

      const response = await api.post<{ success: boolean; message: string }>(
        '/api/user/UpdateUserInfor',
        params,
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '사용자 정보 업데이트에 실패했습니다.');
      }
      
      // 업데이트 후 최신 사용자 정보 반환
      return this.getUserInfo(userToken);
    } catch (error) {
      // 사용자 정보 업데이트 실패 - 프로필 정보 변경 문제 진단을 위해 로그 유지
      console.error('사용자 정보 업데이트 실패:', error);
      throw error;
    }
  }

  async changePassword(userToken: string, newPassword: string): Promise<void> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        '/api/user/ChangePassword',
        { password: newPassword },
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      // 비밀번호 변경 실패 - 보안 정보 변경 문제 진단을 위해 로그 유지
      console.error('비밀번호 변경 실패:', error);
      throw error;
    }
  }

  async updateEmailAddress(userToken: string, email: string, verificationCode: string): Promise<void> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        '/api/user/UpdateMailAddress',
        {
          mail_address: email,
          check_number: verificationCode
        },
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '이메일 주소 업데이트에 실패했습니다.');
      }
    } catch (error) {
      // 이메일 주소 업데이트 실패 - 연락처 정보 변경 문제 진단을 위해 로그 유지
      console.error('이메일 주소 업데이트 실패:', error);
      throw error;
    }
  }

  async verifyMailAddress(userToken: string, email: string): Promise<void> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        `/api/user/SendMailToChangeMailAddress?mail_address=${email}`,
        null,
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '이메일 확인 메일 전송에 실패했습니다.');
      }
    } catch (error) {
      // 이메일 확인 메일 전송 실패 - 연락처 정보 변경 문제 진단을 위해 로그 유지
      console.error('이메일 확인 메일 전송 실패:', error);
      throw error;
    }
  }

  async checkMailAddress(userToken: string, email: string, code: string): Promise<boolean> {
    try {      
      const response = await api.post<{ success: boolean; message: string }>(
        `/api/user/CheckMailAddress?mail_address=${email}&check_number=${code}`,
        null,
        userToken
      );

      if (!response.success) {
        throw new Error(response.message || '이메일 확인 코드 검증에 실패했습니다.');
      }
      
      return response.success;
    } catch (error) {
      // 이메일 확인 코드 검증 실패 - 연락처 정보 변경 문제 진단을 위해 로그 유지
      console.error('이메일 확인 코드 검증 실패:', error);
      throw error;
    }
  }

  async requestEmailChange(userToken: string, newEmail: string): Promise<void> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        '/api/user/email/change-request',
        { email: newEmail },
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '이메일 변경 요청에 실패했습니다.');
      }
    } catch (error) {
      // 이메일 변경 요청 실패 - 연락처 정보 변경 문제 진단을 위해 로그 유지
      console.error('이메일 변경 요청 실패:', error);
      throw error;
    }
  }

  async confirmEmailChange(userToken: string, code: string): Promise<void> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        '/api/user/email/change-confirm',
        { code },
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '이메일 변경 확인에 실패했습니다.');
      }
    } catch (error) {
      // 이메일 변경 확인 실패 - 연락처 정보 변경 문제 진단을 위해 로그 유지
      console.error('이메일 변경 확인 실패:', error);
      throw error;
    }
  }

  async getNotificationSettings(userToken: string): Promise<any> {
    try {
      const response = await api.get<{ success: boolean; message: string; result: any }>(
        '/api/user/notifications',
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '알림 설정 조회에 실패했습니다.');
      }
      
      return response.result;
    } catch (error) {
      // 알림 설정 조회 실패 - 알림 설정 로드 문제 진단을 위해 로그 유지
      console.error('알림 설정 조회 실패:', error);
      throw error;
    }
  }

  async updateNotificationSettings(userToken: string, settings: any): Promise<void> {
    try {
      const response = await api.put<{ success: boolean; message: string }>(
        '/api/user/notifications',
        settings,
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '알림 설정 업데이트에 실패했습니다.');
      }
    } catch (error) {
      // 알림 설정 업데이트 실패 - 알림 설정 변경 문제 진단을 위해 로그 유지
      console.error('알림 설정 업데이트 실패:', error);
      throw error;
    }
  }

  async sendChoicedNumbers(userToken: string, sequenceNo: number): Promise<void> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        `/api/lotto/SendChoicedNumbers?sequence_no=${sequenceNo}`,
        null,
        userToken
      );
      
      if (!response.success) {
        throw new Error(response.message || '선택한 번호 재발송에 실패했습니다.');
      }
    } catch (error) {
      // 선택한 번호 재발송 실패 - 이메일 발송 문제 진단을 위해 로그 유지
      console.error('선택한 번호 재발송 실패:', error);
      throw error;
    }
  }
}

export const userService = new UserService(); 